
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface OrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgToEdit?: { id: string; name: string; region_id?: string } | null;
}

export const OrganizationDialog = ({ open, onOpenChange, orgToEdit }: OrganizationDialogProps) => {
  const [name, setName] = useState("");
  const [regionId, setRegionId] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (orgToEdit) {
      setName(orgToEdit.name);
      setRegionId(orgToEdit.region_id || "");
    } else {
      setName("");
      setRegionId("");
    }
  }, [orgToEdit]);

  const { data: regions } = useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regions")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (orgToEdit) {
        const { error } = await supabase
          .from("organizations")
          .update({ name, region_id: regionId })
          .eq("id", orgToEdit.id);
        
        if (error) throw error;
        toast.success("Organization updated successfully");
      } else {
        const { error } = await supabase
          .from("organizations")
          .insert([{ name, region_id: regionId }]);
        
        if (error) throw error;
        toast.success("Organization created successfully");
      }
      
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      onOpenChange(false);
      setName("");
      setRegionId("");
    } catch (error) {
      toast.error(orgToEdit ? "Failed to update organization" : "Failed to create organization");
      console.error("Error with organization:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{orgToEdit ? "Edit Organization" : "Add New Organization"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Organization Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter organization name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="region">Region</Label>
            <Select value={regionId} onValueChange={setRegionId} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {regions?.map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit">{orgToEdit ? "Update Organization" : "Add Organization"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
